import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, FileText, CheckCircle, Clock, AlertTriangle, Eye, Download } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { ProjectSelector } from '../components/Projects/ProjectSelector';
import { ProgressRing } from '../components/Documents/ProgressRing';
import { DocumentStatusBadge } from '../components/Documents/DocumentStatusBadge';
import { supabase } from '../lib/supabase';
import { useProjectStore } from '../store/projectStore';
import { useDocumentStore } from '../store/documentStore';
import { useAuthStore } from '../store/authStore';
import { useDebounce } from '../hooks/useDebounce';
import { Project, ProfileType } from '../types';
import { DocumentStatus } from '../types/documents';

interface ProjectDocumentSummary {
  project: Project;
  totalDocuments: number;
  verifiedDocuments: number;
  pendingDocuments: number;
  rejectedDocuments: number;
  completionRate: number;
  lastActivity: string;
}

export const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const projects = useProjectStore(state => state.projects);
  const fetchProjects = useProjectStore(state => state.fetchProjects);
  const documents = useDocumentStore(state => state.documents);
  const fetchDocuments = useDocumentStore(state => state.fetchDocuments);
  const verifyDocument = useDocumentStore(state => state.verifyDocument);
  const isLoading = useDocumentStore(state => state.isLoading);

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'COMPLETE' | 'PENDING' | 'INCOMPLETE'>('ALL');
  const [projectSummaries, setProjectSummaries] = useState<ProjectDocumentSummary[]>([]);
  const [isLoadingSummaries, setIsLoadingSummaries] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProject) {
      fetchDocuments(selectedProject.id);
    }
  }, [selectedProject, fetchDocuments]);

  useEffect(() => {
    if (projects.length > 0) {
      calculateProjectSummaries();
    }
  }, [projects]);

  const calculateProjectSummaries = async () => {
    setIsLoadingSummaries(true);
    const summaries: ProjectDocumentSummary[] = [];
    
    try {
      for (const project of projects) {
        // Fetch real document data from database
        const { data: projectDocuments, error } = await supabase
          .from('documents')
          .select('*')
          .eq('project_id', project.id);

        if (error) {
          console.error('Error fetching documents for project:', project.id, error);
          continue;
        }

        const docs = projectDocuments || [];
        const totalDocuments = docs.length;
        const verifiedDocuments = docs.filter(d => d.is_validated === true).length;
        const rejectedDocuments = docs.filter(d => d.is_validated === false).length;
        const pendingDocuments = docs.filter(d => d.is_validated === null).length;
        const completionRate = totalDocuments > 0 ? Math.round((verifiedDocuments / totalDocuments) * 100) : 0;
        
        summaries.push({
          project,
          totalDocuments,
          verifiedDocuments,
          pendingDocuments,
          rejectedDocuments,
          completionRate,
          lastActivity: project.updated_at
        });
      }
    } catch (error) {
      console.error('Error calculating project summaries:', error);
    }
    
    setProjectSummaries(summaries);
    setIsLoadingSummaries(false);
  };

  const filteredSummaries = projectSummaries.filter(summary => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (!summary.project.name.toLowerCase().includes(searchLower) &&
          !summary.project.owner?.full_name?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Status filter
    switch (statusFilter) {
      case 'COMPLETE':
        return summary.completionRate === 100;
      case 'PENDING':
        return summary.pendingDocuments > 0;
      case 'INCOMPLETE':
        return summary.completionRate < 100;
      default:
        return true;
    }
  });

  const handleVerifyDocument = async (documentId: string, status: DocumentStatus, note?: string) => {
    await verifyDocument(documentId, status, note);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      A_PREPARER: 'bg-gray-100 text-gray-800',
      SOUMIS: 'bg-blue-100 text-blue-800',
      ACCORD_PROVISOIRE: 'bg-yellow-100 text-yellow-800',
      VALIDE: 'bg-green-100 text-green-800',
      DEBLOCAGE_FONDS: 'bg-purple-100 text-purple-800',
      EN_COURS: 'bg-indigo-100 text-indigo-800',
      VERIFICATION: 'bg-orange-100 text-orange-800',
      TERMINE: 'bg-emerald-100 text-emerald-800',
      REJETE: 'bg-red-100 text-red-800',
      SUSPENDU: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      A_PREPARER: 'À préparer',
      SOUMIS: 'Soumis',
      ACCORD_PROVISOIRE: 'Accord provisoire',
      VALIDE: 'Validé',
      DEBLOCAGE_FONDS: 'Déblocage fonds',
      EN_COURS: 'En cours',
      VERIFICATION: 'Vérification',
      TERMINE: 'Terminé',
      REJETE: 'Rejeté',
      SUSPENDU: 'Suspendu'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const canManageDocuments = user && [ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType);

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestion des documents</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Suivi et validation des documents de projets
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher un projet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="ALL">Tous les projets</option>
              <option value="COMPLETE">Documents complets</option>
              <option value="PENDING">En attente de validation</option>
              <option value="INCOMPLETE">Documents incomplets</option>
            </select>

            {/* Project Selector */}
            <div>
              <ProjectSelector
                onProjectSelect={setSelectedProject}
                selectedProject={selectedProject}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Projects Overview */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Vue d'ensemble des projets</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Progression documentaire par projet
                </p>
              </div>

              <div className="p-6">
                {isLoadingSummaries ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredSummaries.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Aucun projet trouvé</h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      {searchTerm || statusFilter !== 'ALL' 
                        ? 'Aucun projet ne correspond aux filtres sélectionnés.'
                        : 'Aucun projet disponible pour le moment.'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredSummaries.map((summary) => (
                      <div
                        key={summary.project.id}
                        onClick={() => setSelectedProject(summary.project)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
                          selectedProject?.id === summary.project.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                {summary.project.name}
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(summary.project.status)}`}>
                                {getStatusLabel(summary.project.status)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Porteur: {summary.project.owner?.full_name || summary.project.owner?.email}
                            </p>
                            
                            {/* Progress Bar */}
                            <div className="mt-3">
                              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                                <span>Documents validés</span>
                                <span>{summary.verifiedDocuments}/{summary.totalDocuments}</span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full transition-all duration-500 ${
                                    summary.completionRate === 100 
                                      ? 'bg-green-500' 
                                      : summary.completionRate >= 75 
                                        ? 'bg-blue-500' 
                                        : summary.completionRate >= 50 
                                          ? 'bg-yellow-500' 
                                          : 'bg-red-500'
                                  }`}
                                  style={{ width: `${summary.completionRate}%` }}
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <ProgressRing percent={summary.completionRate} size={50} />
                            <div className="text-right">
                              <div className="flex items-center space-x-1 text-sm">
                                {summary.pendingDocuments > 0 && (
                                  <div className="flex items-center space-x-1 text-orange-600">
                                    <Clock className="w-4 h-4" />
                                    <span>{summary.pendingDocuments}</span>
                                  </div>
                                )}
                                {summary.rejectedDocuments > 0 && (
                                  <div className="flex items-center space-x-1 text-red-600">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span>{summary.rejectedDocuments}</span>
                                  </div>
                                )}
                                <div className="flex items-center space-x-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span>{summary.verifiedDocuments}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Selected Project Documents */}
          <div className="space-y-6">
            {/* Project Info */}
            {selectedProject && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Documents du projet
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedProject.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedProject.owner?.full_name || selectedProject.owner?.email}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedProject.status)}`}>
                      {getStatusLabel(selectedProject.status)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Document Stats */}
            {selectedProject && (
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-lg border border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-6">Statistiques</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-4 bg-gray-700/50 rounded-xl border border-gray-600/50 hover:bg-gray-700/70 transition-colors">
                      <div className="text-3xl font-bold text-green-400 mb-1">
                        {documents.filter(d => d.status === 'VERIFIED').length}
                      </div>
                      <div className="text-sm text-green-300 font-medium">Validés</div>
                    </div>
                    <div className="text-center p-4 bg-gray-700/50 rounded-xl border border-gray-600/50 hover:bg-gray-700/70 transition-colors">
                      <div className="text-3xl font-bold text-orange-400 mb-1">
                        {documents.filter(d => d.status === 'UPLOADED').length}
                      </div>
                      <div className="text-sm text-orange-300 font-medium">En attente</div>
                    </div>
                  </div>

                  <div className="text-center p-4 bg-gray-700/50 rounded-xl border border-gray-600/50 hover:bg-gray-700/70 transition-colors">
                    <div className="text-3xl font-bold text-red-400 mb-1">
                      {documents.filter(d => d.status === 'REJECTED').length}
                    </div>
                    <div className="text-sm text-red-300 font-medium">Rejetés</div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            {selectedProject && canManageDocuments && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Actions rapides</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/projects/${selectedProject.id}/documents`)}
                    className="w-full flex items-center space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700 dark:text-blue-300 font-medium">
                      Gérer les documents du projet
                    </span>
                  </button>

                  <button
                    onClick={() => navigate(`/projects/${selectedProject.id}/documents/preview`)}
                    className="w-full flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-900/20 hover:bg-gray-100 dark:hover:bg-gray-900/40 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      Prévisualiser tous les documents
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected Project Documents Detail */}
        {selectedProject && documents.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Documents de "{selectedProject.name}"
              </h2>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {documents.map((document) => (
                  <div
                    key={document.id}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{document.filename}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {document.category} • {document.file_size ? (document.file_size / (1024 * 1024)).toFixed(2) + ' MB' : 'Taille inconnue'}
                        </p>
                        {document.uploaded_at && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Déposé le {new Date(document.uploaded_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <DocumentStatusBadge status={document.status} />
                      
                      <div className="flex items-center space-x-1">
                        {document.file_url && (
                          <button
                            onClick={() => window.open(document.file_url, '_blank')}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-lg transition-colors"
                            title="Voir le document"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        
                        {canManageDocuments && document.status === 'UPLOADED' && (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleVerifyDocument(document.id, DocumentStatus.VERIFIED)}
                              className="px-3 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors"
                              title="Valider"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => handleVerifyDocument(document.id, DocumentStatus.REJECTED, 'Document non conforme')}
                              className="px-3 py-1 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 transition-colors"
                              title="Rejeter"
                            >
                              ✗
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Global Progress Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Résumé global</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {projectSummaries.length}
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Projets total</div>
            </div>
            
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {projectSummaries.filter(s => s.completionRate === 100).length}
              </div>
              <div className="text-sm text-green-700 dark:text-green-300">Documents complets</div>
            </div>
            
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {projectSummaries.filter(s => s.pendingDocuments > 0).length}
              </div>
              <div className="text-sm text-orange-700 dark:text-orange-300">En attente validation</div>
            </div>
            
            <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {projectSummaries.reduce((acc, s) => acc + s.rejectedDocuments, 0)}
              </div>
              <div className="text-sm text-red-700 dark:text-red-300">Documents rejetés</div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};